(() => {
  const key = 'maison_localization_v1';
  const read = () => {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
  };
  const write = (value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
    try { sessionStorage.setItem(key, '1'); } catch {}
  };
  const suppressed = () => {
    const value = read();
    if (value && value.until > Date.now()) return true;
    try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
  };
  const remember = (days, country, language, reason) => write({ until: Date.now() + days * 86400000, country, language, reason });
  const languageBase = (value) => String(value || '').toLowerCase().split('-')[0];
  class MaisonLocalization {
    constructor(root) {
      this.root = root;
      this.events = new AbortController();
      this.dialog = root.querySelector('dialog');
      this.form = root.querySelector('form');
      this.country = root.querySelector('[name="country_code"]');
      this.language = root.querySelector('[name="language_code"]');
      this.data = JSON.parse(root.querySelector('[data-config]').textContent);
      this.countries = Array.from(this.country.options).map(o => o.value);
      this.languages = Array.from(this.language.options).map(o => o.value);
      this.copies = { en: this.data.fallback, ...this.data.copies };
      const primaryLanguage = languageBase(navigator.languages?.[0] || navigator.language);
      this.uiLanguage = this.copies[primaryLanguage] ? primaryLanguage : 'en';
      this.preferredLanguage = this.languages.find(value => languageBase(value) === this.uiLanguage) || this.languages.find(value => languageBase(value) === 'en') || this.data.language;
      this.translate();
      this.filterLanguages(this.preferredLanguage);
      const on = (el, event, callback) => el.addEventListener(event, callback, { signal: this.events.signal });
      on(root.querySelector('[data-open]'), 'click', () => {
        this.interacting = true;
        clearTimeout(this.timer);
        this.country.value = this.data.country;
        this.filterLanguages(this.data.language);
        this.open();
      });
      on(root.querySelector('[data-close]'), 'click', () => this.dismiss());
      on(root.querySelector('[data-stay]'), 'click', () => this.dismiss());
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.dismiss(); });
      const takeControl = () => { this.interacting = true; clearTimeout(this.timer); };
      on(this.form, 'pointerdown', takeControl);
      on(this.form, 'keydown', takeControl);
      on(this.dialog, 'pointerdown', event => {
        const rect = this.dialog.getBoundingClientRect();
        this.backdropDown = event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
      });
      on(this.dialog, 'pointerup', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (this.backdropDown && event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dismiss();
        this.backdropDown = false;
      });
      on(this.country, 'change', () => { takeControl(); this.filterLanguages(this.language.value); });
      on(this.language, 'change', takeControl);
      on(this.form, 'submit', () => {
        const days = root.querySelector('[data-remember]').checked ? this.data.choiceDays : 0;
        remember(days, this.country.value, this.language.value, 'choice');
        this.form.querySelector('[name="return_to"]').value = location.pathname + location.search;
      });
      on(document, 'submit', event => {
        const form = event.target;
        if (form === this.form || !form.matches('form')) return;
        const action = new URL(form.action, location.origin);
        if (action.pathname.endsWith('/localization')) {
          remember(this.data.choiceDays, form.querySelector('[name="country_code"]')?.value || this.data.country, form.querySelector('[name="language_code"]')?.value || this.data.language, 'native-choice');
        }
      });
      on(document, 'click', event => {
        const link = event.target.closest('localization-form a[data-value][hreflang]');
        if (!link) return;
        remember(this.data.choiceDays, this.data.country, link.dataset.value, 'native-choice');
        this.interacting = true;
        clearTimeout(this.timer);
        this.banner?.remove();
      });
      if (this.data.designMode && this.data.editorPreview) {
        this.filterLanguages(this.preferredLanguage);
        this.open();
      } else if (this.data.auto && !suppressed()) {
        this.detect();
      }
    }
    translate() {
      const copy = this.copies[this.uiLanguage] || this.copies.en;
      this.dialog.lang = this.uiLanguage;
      this.root.querySelectorAll('[data-copy]').forEach(el => {
        if (copy[el.dataset.copy]) el.textContent = copy[el.dataset.copy];
      });
      this.root.querySelector('[data-close]').setAttribute('aria-label', copy.close);
      try {
        const names = new Intl.DisplayNames([this.uiLanguage], { type: 'region' });
        Array.from(this.country.options).forEach(o => { o.textContent = names.of(o.value); });
      } catch {}
    }
    filterLanguages(preferred) {
      const options = Array.from(this.language.options);
      options.forEach(option => { option.hidden = false; option.disabled = false; });
      const selected = options.find(o => languageBase(o.value) === languageBase(preferred)) || options.find(o => languageBase(o.value) === this.uiLanguage) || options.find(o => o.value === 'en') || options[0];
      if (selected) this.language.value = selected.value;
    }
    detect() {
      this.country.value = this.data.country;
      this.filterLanguages(this.preferredLanguage);
      if (languageBase(this.language.value) === languageBase(this.data.language)) return;
      this.timer = setTimeout(() => this.offer(), Math.max(0, this.data.delay * 1000));
    }
    offer() {
      if (!this.root.isConnected || this.interacting || suppressed()) return;
      const visible = element => {
        if (element === this.dialog || !element.getClientRects().length) return false;
        const details = element.closest('details');
        if (details && !details.open) return false;
        for (let parent = element; parent; parent = parent.parentElement) {
          const style = getComputedStyle(parent);
          if (parent.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
        }
        return true;
      };
      const other = Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])')).some(visible);
      if (document.visibilityState !== 'visible' || other) {
        this.timer = setTimeout(() => this.offer(), 1000);
        return;
      }
      this.showSuggestion();
    }
    showSuggestion() {
      if (this.banner) return;
      const copy = this.copies[this.uiLanguage] || this.copies.en;
      const banner = document.createElement('aside');
      banner.className = 'mp-language-suggestion';
      banner.lang = this.uiLanguage;
      banner.setAttribute('aria-label', copy.language);
      const label = document.createElement('span');
      label.textContent = this.language.selectedOptions[0].textContent;
      const accept = document.createElement('button');
      accept.type = 'button';
      accept.textContent = copy.continue_label;
      const stay = document.createElement('button');
      stay.type = 'button';
      stay.textContent = copy.stay;
      accept.addEventListener('click', () => {
        this.country.value = this.data.country;
        this.form.requestSubmit();
      });
      stay.addEventListener('click', () => {
        remember(this.data.dismissDays, this.data.country, this.data.language, 'dismiss');
        banner.remove();
      });
      banner.append(label, accept, stay);
      this.banner = banner;
      this.root.append(banner);
    }
    open() {
      if (this.dialog.open) return;
      this.banner?.remove();
      this.previousFocus = document.activeElement;
      this.dialog.showModal();
    }
    dismiss() {
      this.interacting = true;
      clearTimeout(this.timer);
      if (!this.data.designMode) remember(this.data.dismissDays, this.data.country, this.data.language, 'dismiss');
      this.dialog.close();
      this.previousFocus?.focus?.();
    }
    destroy() {
      clearTimeout(this.timer);
      if (this.dialog.open) this.dialog.close();
      this.banner?.remove();
      this.events.abort();
    }
  }
  const initialize = (scope = document) => {
    const roots = scope.matches?.('[data-maison-localization]') ? [scope] : scope.querySelectorAll('[data-maison-localization]');
    roots.forEach(root => { if (!root.maisonLocalization) root.maisonLocalization = new MaisonLocalization(root); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelector('[data-maison-localization]')?.maisonLocalization?.destroy());
})();
